/* Atlas V3 Desktop — Settings. Theme (Day/Night/Auto via theme.js),
   Display + Alerts + Defaults prefs (useUserPref, server-synced when signed
   in), Account (Clerk), About. Mirrors the mobile Settings sections.
   IMPORTANT: pref VALUES must match mobile's stored vocabulary exactly
   ('home'/'watch', 'move'/'pct'/'az', 'push'/'email') — same user_prefs keys
   are read by both clients. */
import { useState } from 'react'
import { useUser, useClerk } from '@clerk/clerk-react'
import { getStoredMode, setThemeMode, resolveTheme } from '@/v2/v3/theme.js'
import { useUserPref } from '@/v2/hooks/useUserPref'
import { AgentAccess } from '@/v2/components/AgentAccess'
import pkg from '../../../package.json'

const Mono = { fontFamily: 'var(--studio-font-mono)' }

function Eyebrow({ children, first }) {
  return <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'hsl(var(--studio-accent))', margin: first ? '4px 0 10px' : '28px 0 10px' }}>{children}</div>
}
function Row({ label, sub, right, first }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 2px', borderTop: first ? 'none' : '1px solid hsl(var(--studio-border) / 0.45)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{label}</div>
        {sub && <div style={{ fontSize: 11.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 3 }}>{sub}</div>}
      </div>
      {right}
    </div>
  )
}
function Toggle({ on, onClick }) {
  return (
    <button onClick={onClick} style={{ all: 'unset', cursor: 'pointer', width: 40, height: 23, borderRadius: 999, background: on ? 'hsl(var(--studio-accent) / 0.35)' : 'hsl(var(--studio-surface-2))', border: `1px solid hsl(var(--studio-${on ? 'accent' : 'border'}) / ${on ? 0.6 : 1})`, position: 'relative', transition: 'background 160ms' }}>
      <span style={{ position: 'absolute', top: 2, left: on ? 19 : 2, width: 17, height: 17, borderRadius: '50%', background: on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))', transition: 'left 160ms' }} />
    </button>
  )
}
// options: [{ v: storedValue, l: label }] — stored value ≠ display label so the
// desktop can show OVERVIEW/MY STOCKS while writing mobile's 'home'/'watch'.
function Seg({ value, options, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 3, background: 'hsl(var(--studio-surface))', borderRadius: 999, padding: 3 }}>
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} style={{ all: 'unset', cursor: 'pointer', ...Mono, fontSize: 10, letterSpacing: '0.06em', padding: '5px 11px', borderRadius: 999, color: value === o.v ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))', background: value === o.v ? 'hsl(var(--studio-surface-2))' : 'transparent' }}>{o.l}</button>
      ))}
    </div>
  )
}

export function Settings() {
  const { user } = useUser()
  const { signOut } = useClerk()
  const [mode, setMode] = useState(() => getStoredMode())
  const applyMode = (m) => { setThemeMode(m); setMode(m) }

  // haptics_enabled stays a mobile-only pref — no desktop row for it.
  const [livePulse, setLivePulse] = useUserPref('live_pulse_enabled', true)
  const [fridayReveal, setFridayReveal] = useUserPref('friday_reveal_enabled', true)
  const [aSignals, setASignals] = useUserPref('alert_signals_enabled', true)
  const [aPrice, setAPrice] = useUserPref('alert_price_enabled', true)
  const [aBrief, setABrief] = useUserPref('alert_briefing_enabled', true)
  const [delivery, setDelivery] = useUserPref('alert_delivery', 'push')
  const [defaultTab, setDefaultTab] = useUserPref('default_tab', 'home')
  const [defaultSort, setDefaultSort] = useUserPref('default_sort', 'move')

  const swatch = (m, label) => {
    const active = mode === m
    const night = resolveTheme(m) === 'night'
    return (
      <button onClick={() => applyMode(m)} style={{ all: 'unset', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
        <div style={{ width: 62, height: 44, borderRadius: 6, border: `1.5px solid hsl(var(--studio-${active ? 'accent' : 'border'}))`, background: night ? '#1a1730' : '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ width: 22, height: 22, borderRadius: '50%', background: night ? 'hsl(247 65% 64%)' : 'hsl(var(--studio-accent))' }} />
        </div>
        <span style={{ ...Mono, fontSize: 10, letterSpacing: '0.08em', color: active ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))' }}>{label}</span>
      </button>
    )
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <div className="v3web-phead">
        <div className="h" style={{ fontStyle: 'italic' }}>Settings</div>
      </div>

      <div className="v3web-setgrid">
        {/* Left column — Appearance / Display / Alerts */}
        <div>
          <Eyebrow first>Appearance</Eyebrow>
          <div style={{ display: 'flex', gap: 18, padding: '6px 2px 4px' }}>
            {swatch('ink', 'Day')}
            {swatch('night', 'Night')}
            {swatch('auto', 'Auto')}
          </div>

          <Eyebrow>Display</Eyebrow>
          <Row first label="Live pulse" sub="Animate the index chart's live dot" right={<Toggle on={!!livePulse} onClick={() => setLivePulse(!livePulse)} />} />
          <Row label="Friday reveal" sub="Notify when the weekly briefing lands" right={<Toggle on={!!fridayReveal} onClick={() => setFridayReveal(!fridayReveal)} />} />

          <Eyebrow>Alerts</Eyebrow>
          <Row first label="Signal alerts" sub="Options flow, insider trades, 8-Ks" right={<Toggle on={!!aSignals} onClick={() => setASignals(!aSignals)} />} />
          <Row label="Price alerts" sub="Big moves on your names" right={<Toggle on={!!aPrice} onClick={() => setAPrice(!aPrice)} />} />
          <Row label="Briefing alerts" sub="The nightly Atlas Intel" right={<Toggle on={!!aBrief} onClick={() => setABrief(!aBrief)} />} />
          <Row label="Deliver via" right={<Seg value={delivery} options={[{ v: 'push', l: 'PUSH' }, { v: 'email', l: 'EMAIL' }]} onChange={setDelivery} />} />
        </div>

        {/* Right column — Defaults / Account / About */}
        <div>
          <Eyebrow first>Defaults</Eyebrow>
          <Row first label="Open to" sub="The tab Atlas lands on at launch" right={<Seg value={defaultTab} options={[{ v: 'home', l: 'OVERVIEW' }, { v: 'watch', l: 'MY STOCKS' }]} onChange={setDefaultTab} />} />
          <Row label="Default sort" right={<Seg value={defaultSort} options={[{ v: 'move', l: 'MOVE' }, { v: 'pct', l: '%' }, { v: 'az', l: 'A–Z' }]} onChange={setDefaultSort} />} />

          <Eyebrow>Account</Eyebrow>
          <Row first label={user?.fullName || user?.username || 'Signed in'} sub={user?.primaryEmailAddress?.emailAddress} right={<button onClick={() => signOut()} style={{ all: 'unset', cursor: 'pointer', ...Mono, fontSize: 11, letterSpacing: '0.06em', color: 'hsl(var(--studio-danger))' }}>SIGN OUT</button>} />

          <Eyebrow>Agent Access</Eyebrow>
          <AgentAccess />

          <Eyebrow>About</Eyebrow>
          <Row first label="Version" right={<span style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-text-subtle))' }}>v{pkg.version}</span>} />
          <Row label="stonk.world" right={<a href="https://stonk.world" style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-accent))', textDecoration: 'none' }}>Visit →</a>} />
        </div>
      </div>
    </div>
  )
}
